import { http } from '@pendo/http';
import get from 'lodash/get';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';
import isString from 'lodash/isString';
import toString from 'lodash/toString';
import { AppsService as Apps } from '@/common/apps/apps.service';
import getDevicePropsArr from '@/constants/device-props';
import { MetadataTypeId } from '@/types/analytics/analytics.types';
import { getI18nKeyForMetadataDisplayName } from '@/utils/i18n/i18n-metadata';

export const MAX_DAILY_ENABLE_HISTORICAL = 3;

export const MOBILE_QUERY_FIELDS = {
    name: 'lastdevicemodel',
    version: 'lastosversion',
    appVersion: 'lastappversion',
    sdkVersion: 'lastsdkversion',
};

export const FIELDS_WITH_APP_ID_SUPPORT = Object.values(MOBILE_QUERY_FIELDS);

export function fetchVisitorMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/visitor/schema?cardinality=${cardinality}`);
}

export function fetchAccountMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/account/schema?cardinality=${cardinality}`);
}

export function fetchParentAccountMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/parentAccount/schema?cardinality=${cardinality}`);
}

export function fetchEventMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/event/schema?cardinality=${cardinality}`).then((res) => {
        if (get(res, 'data.event', null)) {
            res.data.event = parseEventMetadataUniqueValues(res.data.event);
        }

        return res;
    });
}

export function retrieveMetadataByField (kind, group, fieldName, abortSignal) {
    return http.get(`/api/s/_SID_/metadata/${kind}/${group}/schema/${fieldName}`, { signal: abortSignal });
}

export function getSchemaForGroup ({ kind, group }) {
    return http.get(`/api/s/_SID_/metadata/${kind}/${group}/schema`);
}

export function getSchemaForKind (kind) {
    return http.get(`/api/s/_SID_/metadata/${kind}/schema`);
}

export function promoteSchema ({ kind, group, field }, promote) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/promotion`, promote);
}

export function updateSchema ({ kind, group, field }, data) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}`, data);
}

export function removeSchema ({ kind, group, field }) {
    return http.delete(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}`);
}

export function updateRules ({ kind, group, field }, body) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/eventurlrules`, body);
}

export function normalizeUrl (url) {
    // if the URL already contains encoded characters, we need to decode them first so they
    // don't get encoded again below. Malformed percent-encoding (e.g. bare `%`) throws
    // URIError; in that case the URL wasn't encoded so we pass it through.
    let decoded;
    try {
        decoded = decodeURIComponent(url);
    } catch {
        decoded = url;
    }

    return http.post('/api/s/_SID_/helper/urlnorm', { url: encodeURI(decoded) });
}

export function parseUrlRule (rule) {
    return http.post('/api/s/_SID_/helper/urlpatternparse', { urlPattern: rule });
}

// you MUST pass `{}` as the body here. http strips the content-type header when
// posting with an empty body. failure to pass a body will result in 415 from pendo BE
export function updateViaEnabled ({ kind, group, field }) {
    return http.post(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/viaenabled`, {});
}

export function setObjectProperty ({ kind, group, field }, body) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/objectproperty`, body);
}

export function parseEventMetadataUniqueValues (eventMetadata) {
    return Object.entries(eventMetadata).reduce((acc, [key, value]) => {
        // each value in uniqueValues is JSON Stringified due to how it is stored in the BE
        acc[key] = {
            ...value,
            uniqueValues: get(value, 'uniqueValues', []).map((uniqueValue) => {
                try {
                    return JSON.parse(uniqueValue);
                } catch {
                    return uniqueValue;
                }
            }),
        };

        return acc;
    }, {});
}

export function isHistoricalMetadata (metadata) {
    const { isPromoted } = metadata;

    return isPromoted;
}

export function isMobileDeviceProp ({ field, app } = {}) {
    if(!app) return false;

    const isMobile = Apps.isMobile(app);

    if(!isMobile) return false;

    return !!getDevicePropsArr({ appId: app.id }).find((prop) => prop.field === field);
}

export function queryMetadataValues (kind, group, field, params = {}) {
    if (params.app === '*' || group !== 'auto' || !FIELDS_WITH_APP_ID_SUPPORT.includes(field)) {
        delete params.app;
    }

    return http.get(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/values`, {
        params,
    });
}

export function iterateThroughMetadata (metadata, kind) {
    if (!metadata) return [];
    const doNotFlatten = ['utm'];

    return Object.keys(metadata).reduce((schemas, group) => {
        Object.keys(metadata[group]).forEach((field) => {
            if (doNotFlatten.includes(group) && kind !== 'utm') {
                return;
            }
            const displayName = metadata[group][field].DisplayName;
            schemas.push({
                ...metadata[group][field],
                group,
                field,
                kind,
                DisplayName: setDisplayName(displayName, group, field, kind),
                groupDisplayName: group === 'segmentio' ? 'Segment.io' : group,
            });
        });

        return schemas;
    }, []);
}

export function setDisplayName (displayName, group, field, kind) {
    if (group !== 'auto') {
        const valueToReturn = displayName && displayName !== '' ? displayName : field;

        return valueToReturn;
    }

    return getI18nKeyForMetadataDisplayName(group, field, kind);
}

export function filterEventPropList ({
    metadata,
    kind = 'event',
    types = undefined,
    cardinalityLimit = undefined,
    epNames,
    uniqueBetweenTypes = false,
    trimUniqueValues = false,
} = {}) {
    const kindFilter = kind === MetadataTypeId.Utm ? 'event' : kind;

    return iterateThroughMetadata(get(metadata, kindFilter, []), kind).reduce((acc, metadata) => {
        const { cardinality, field, Type } = metadata;
        const isBoolean = Type === 'boolean';
        const isTypeIncluded = isNil(types) || types.includes(Type);
        const isEpNameIncluded = isNil(epNames) || epNames.includes(field);
        const isValidCardinality = isNil(cardinalityLimit) || (cardinality <= cardinalityLimit && cardinality > 0);

        const allowed = isTypeIncluded && isEpNameIncluded && (isValidCardinality || isBoolean);
        if (uniqueBetweenTypes && metadata.uniqueValues?.map) {
            const sanitizedMetadata = metadata.uniqueValues.map((uniqueValue) => {
                return isObject(uniqueValue) && has(uniqueValue, 'toString')
                    ? omit(uniqueValue, 'toString')
                    : uniqueValue;
            });
            metadata.uniqueValues = uniqBy(sanitizedMetadata, toString);
        }

        if (trimUniqueValues && metadata.uniqueValues?.map) {
            metadata.uniqueValues = uniq(
                metadata.uniqueValues.map((uniqueValue) => {
                    return isString(uniqueValue) ? uniqueValue.trim() : uniqueValue;
                }),
            );
        }

        if (allowed) {
            acc.push(metadata);
        }

        return acc;
    }, []);
}

export function queryExternalMetadataValues ({ kind, externalGroup, field }) {
    return http
        .get(`/api/s/_SID_/metadata/${kind}/${externalGroup}/schema/${field}/values/external`)
        .then((res) => res.data)
        .catch((err) => {
            throw err;
        });
}

export function generateMetadataDescription ({ kind, group, field, displayName, type, sample, description }) {
    return http.post(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/generatedescription`, {
        DisplayName: displayName,
        Type: type,
        sample,
        description,
    });
}
